import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText } from '../components/ui.js';
import { DELIVERY_STATUS, period, RECONCILIATION_STATUS, type DeliveryStatus, type ReconciliationStatus, type StatementStatus } from '../format.js';

export interface Review {
  status: StatementStatus;
  months: Array<{ year: number; month: number; status: ReconciliationStatus }>;
  recipients: string[];
  copyTo: string[];
  from: string | null;
  preview: { sha256: string; filename: string; createdAt: string } | null;
  rejection: { at: string; reason: string } | null;
  blockers: Array<{ code: string; message: string }>;
}

export interface Delivery {
  id: string;
  attempt: number;
  status: DeliveryStatus;
  messageId: string;
  toAddresses: string[];
  bccAddresses: string[];
  subject: string;
  tries: number;
  sentAt: string | null;
  lastError: string | null;
  requestedByLabel: string;
  createdAt: string;
  events: Array<{ id: string; type: string; recipient: string | null; statusCode: string | null; detail: string | null; occurredAt: string }>;
}

interface ReviewTarget {
  id: string;
  isHouse: boolean;
  status: StatementStatus;
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.message ?? e.code) : fallback);
const when = (iso: string) => new Date(iso).toLocaleString('en-GB');
const useRefresh = () => {
  const client = useQueryClient();
  return () => Promise.all(['statements', 'payments'].map((k) => client.invalidateQueries({ queryKey: [k] })));
};

/**
 * Review of a draft: reconciliation of the covered months, recipients, the draft PDF and the reasons that
 * block approval. Approving assigns the number, writes the final documents and mails them; rejecting keeps
 * the draft with the reason.
 */
export function StatementReview({ s }: { s: ReviewTarget }) {
  const isAdmin = useMe().user?.role === 'admin';
  const refresh = useRefresh();
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const review = useQuery({ queryKey: ['statements', 'review', s.id], queryFn: () => api<Review>('GET', `/api/statements/${s.id}/review`) });
  const approve = useMutation({
    mutationFn: () => api<{ number: string | null }>('POST', `/api/statements/${s.id}/approve`, review.data?.preview ? { previewSha256: review.data.preview.sha256 } : {}),
    onSuccess: async () => {
      setConfirming(false);
      await refresh();
    },
  });
  const reject = useMutation({
    mutationFn: () => api('POST', `/api/statements/${s.id}/reject`, { reason }),
    onSuccess: async () => {
      setRejecting(false);
      setReason('');
      await refresh();
    },
  });
  if (review.isError) return <ErrorText>{errorText(review.error, 'The review could not be loaded')}</ErrorText>;
  const r = review.data;
  if (!r) return null;
  const blockers = r.blockers.filter((b) => b.code !== 'statement_not_draft');
  return (
    <div className="space-y-3 rounded border border-amber-300 p-3 dark:border-amber-800">
      <h2 className="font-medium">Review</h2>
      {r.rejection && (
        <p className="text-sm text-red-700 dark:text-red-400">
          Rejected {when(r.rejection.at)}: {r.rejection.reason}
        </p>
      )}
      <table className="text-sm">
        <thead className="text-slate-500">
          <tr><th className="pr-8 text-left">Covered month</th><th className="text-left">Reconciliation</th></tr>
        </thead>
        <tbody>
          {r.months.map((m) => (
            <tr key={period(m)}>
              <td className="pr-8">{period(m)}</td>
              <td className={RECONCILIATION_STATUS[m.status].className}>{RECONCILIATION_STATUS[m.status].label}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!s.isHouse && (
        <p className="text-sm">
          Approval mails the final PDF and XLSX from <span className="font-mono">{r.from ?? '(mail not configured)'}</span> to{' '}
          <span className="font-mono">{r.recipients.length ? r.recipients.join(', ') : '(no contact email)'}</span>
          {r.copyTo.length > 0 && <>, Bcc <span className="font-mono">{r.copyTo.join(', ')}</span></>}.
        </p>
      )}
      {s.isHouse && <p className="text-sm text-slate-500">SilentFuture's own statement is approved without documents or mail.</p>}
      {r.preview && (
        <iframe title="Draft preview" className="h-[36rem] w-full max-w-4xl rounded border border-slate-200 dark:border-slate-800" src={`/api/statements/${s.id}/documents/pdf?inline=1&v=${r.preview.sha256.slice(0, 12)}`} />
      )}
      {blockers.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-red-700 dark:text-red-400">
          {blockers.map((b) => <li key={b.code}>{b.message}</li>)}
        </ul>
      )}
      {isAdmin && s.status === 'draft' && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {!confirming && <Button onClick={() => setConfirming(true)} disabled={blockers.length > 0}>{s.isHouse ? 'Approve' : 'Approve and send'}</Button>}
            {confirming && (
              <>
                <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
                  {approve.isPending ? 'Approving...' : s.isHouse ? 'Confirm approval' : `Confirm: number, final documents and mail to ${r.recipients.join(', ')}`}
                </Button>
                <Button className="bg-slate-500" onClick={() => setConfirming(false)}>Cancel</Button>
              </>
            )}
            <Button className="bg-red-700 hover:bg-red-600 dark:bg-red-300" onClick={() => setRejecting(!rejecting)}>Reject</Button>
          </div>
          {rejecting && (
            <form className="max-w-2xl space-y-2" onSubmit={(e) => { e.preventDefault(); reject.mutate(); }}>
              <label className="block text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-400">Reason (the draft stays a draft; its preview is removed)</span>
                <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <Button type="submit" disabled={reject.isPending || reason.trim().length < 10}>Reject draft</Button>
            </form>
          )}
          {approve.isError && <ErrorText>{errorText(approve.error, 'The approval failed')}</ErrorText>}
          {reject.isError && <ErrorText>{errorText(reject.error, 'The rejection failed')}</ErrorText>}
        </div>
      )}
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  queued: 'Queued', sending: 'Sending', sent: 'Accepted by the mail server', recipient_rejected: 'Recipient rejected', smtp_error: 'SMTP error',
  retry_scheduled: 'Retry scheduled', unconfirmed: 'Outcome unknown', confirmed_by_copy: 'Confirmed by the mailbox copy', failed: 'Failed',
  bounce: 'Bounced', delay: 'Delayed', delivered: 'Delivered', complaint: 'Complaint',
};
const MAILABLE: StatementStatus[] = ['approved', 'sent', 'invoice_received', 'booked', 'paid'];

/** Delivery log: every mail attempt of the statement with its events; admins can resend. */
export function StatementDeliveries({ s }: { s: ReviewTarget }) {
  const isAdmin = useMe().user?.role === 'admin';
  const refresh = useRefresh();
  const log = useQuery({ queryKey: ['statements', 'deliveries', s.id], queryFn: () => api<Delivery[]>('GET', `/api/statements/${s.id}/deliveries`) });
  const resend = useMutation({ mutationFn: () => api('POST', `/api/statements/${s.id}/deliveries`), onSuccess: refresh });
  if (log.isError) return <ErrorText>{errorText(log.error, 'The delivery log could not be loaded')}</ErrorText>;
  const deliveries = log.data ?? [];
  const latest = deliveries[0];
  const open = latest && ['queued', 'sending', 'unconfirmed'].includes(latest.status);
  return (
    <div className="space-y-2">
      <h2 className="font-medium">Delivery</h2>
      {latest && DELIVERY_STATUS[latest.status].problem && (
        <p role="alert" className="text-sm font-semibold text-red-700 dark:text-red-400">
          {DELIVERY_STATUS[latest.status].label}: {latest.lastError ?? latest.events.filter((e) => ['bounce', 'complaint', 'recipient_rejected', 'failed'].includes(e.type)).map((e) => `${e.recipient ?? ''} ${e.statusCode ?? ''} ${e.detail ?? ''}`.trim()).join('; ')}
        </p>
      )}
      {deliveries.length === 0 && <p className="text-sm text-slate-500">Not mailed.</p>}
      {deliveries.map((d) => (
        <details key={d.id} open={d === latest} className="text-sm">
          <summary>
            Attempt {d.attempt}: <span className={DELIVERY_STATUS[d.status].className}>{DELIVERY_STATUS[d.status].label}</span> to {d.toAddresses.join(', ')}
            {d.sentAt ? `, sent ${when(d.sentAt)}` : ''} ({d.requestedByLabel})
          </summary>
          <p className="text-slate-500">Bcc {d.bccAddresses.join(', ')}; Message-ID <span className="font-mono">{d.messageId}</span>; subject "{d.subject}"</p>
          <ul>
            {d.events.map((e) => (
              <li key={e.id}>
                {when(e.occurredAt)} {EVENT_LABEL[e.type] ?? e.type}
                {e.recipient ? ` ${e.recipient}` : ''}
                {e.statusCode ? ` (${e.statusCode})` : ''}
                {e.detail ? `: ${e.detail}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ))}
      {isAdmin && !s.isHouse && MAILABLE.includes(s.status) && (
        <Button onClick={() => resend.mutate()} disabled={resend.isPending || Boolean(open)}>
          {deliveries.length ? 'Resend to the current partner contacts' : 'Send to the partner contacts'}
        </Button>
      )}
      {resend.isError && <ErrorText>{errorText(resend.error, 'The mail could not be queued')}</ErrorText>}
    </div>
  );
}
